/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { InjectedFormProps, reduxForm, getFormValues } from 'redux-form';
import { bindActionCreators, Dispatch } from 'redux';

import { css } from 'aphrodite';
import { ActionButton, colors, ActionButtonOption } from '@blend/consumer-components';
import { LoadingSpinner } from '@blend/consumer-components';
import { State } from '../../redux';
import {
  submitCurrentTask as submitCurrentTaskAction,
  backToUserSelect as backToUserSelectAction,
} from '../../actions/taskActions';
import { participantAPI } from '../../api/participant';
import { BackButton } from '../../pages/TasksPage/BackButton/BackButton';
import { ConsumerAppConfigContext, useConsumerTheme } from '../../hooks/useConsumerTheme';
import { TaskMessages } from './TaskMessages/TaskMessages';
import { ContextualHelpList } from './ContextualHelpList/ContextualHelpList';
import { TaskForm } from './TaskForm';
import { templateTypeMap, TaskTemplateType } from './config';
import { styles as taskStyles } from './styles';
import { Wrapper as InputErrorWrapper, Error } from '@components/form/Inputs/InputError';
import { DefaultSubmitInput } from '@components/form/DefaultSubmitInput/DefaultSubmitInput';
import { AlertBox } from '@components/AlertBox/AlertBox';
import { onboardingPerformanceTracker } from '@utils/onboardingPerformanceTracker';

export const FORM_NAME = 'task';

const GENERAL_ERROR_MESSAGE =
  'There was a problem saving the information on this page. ' +
  'Refresh the page and try again, or contact us if you still have trouble.';

export namespace ConnectedTaskForm {
  export interface DispatchProps {
    submitCurrentTask: (nextTask: any) => void;
    backToUserSelect: () => void;
  }
  export interface OwnProps {
    showBackButton: boolean;
    hideMessage: boolean;
    ctaButtonStyle?: ActionButtonOption;
  }
  export interface StateProps {
    task: State['tasks']['current'];
    values: any;
  }
  export type Props = InjectedFormProps<{}, StateProps & OwnProps & DispatchProps> &
    StateProps &
    OwnProps &
    DispatchProps;
}

const generateStyles = (_appConfig: ConsumerAppConfigContext) => taskStyles;

export const UnconnectedTaskForm: React.FC<ConnectedTaskForm.Props> = ({
  handleSubmit,
  submitCurrentTask,
  task,
  reset,
  showBackButton,
  backToUserSelect,
  hideMessage,
  ctaButtonStyle = 'primary',
}) => {
  const {
    messages,
    inputs,
    contextualHelps,
    type,
    hideContinueButton,
    showExitButton = false,
    submitText = 'Continue',
    alertInfo,
  } = task.template;
  const [generalError, setGeneralError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [disableContinueButton, setDisableContinueButton] = useState(false);
  const { styles } = useConsumerTheme(generateStyles);

  const isButtonDisabled = submitting || disableContinueButton;

  const customSubmit = useCallback(
    async (values: {}) => {
      setSubmitting(true);
      setGeneralError('');

      try {
        const { task: nextTask } = await participantAPI.tasks.submitTask(task.id, values);

        // Track successful task completion
        onboardingPerformanceTracker.completeStep(task.id, task.templateId, true);

        reset();
        submitCurrentTask(nextTask);
      } catch (error: unknown) {
        // Track failed task completion
        onboardingPerformanceTracker.completeStep(task.id, task.templateId, false);

        setGeneralError(GENERAL_ERROR_MESSAGE);
        setSubmitting(false);
      }
    },
    [task],
  );

  const setContinueButtonDisabled = useCallback((value: boolean) => {
    setDisableContinueButton(value);
  }, []);

  // set submitting to false if we successfully transition tasks
  useEffect(() => {
    setSubmitting(false);
  }, [task]);

  if (!templateTypeMap[type as TaskTemplateType]) {
    // TODO: Error reporting here
    return null;
  }

  const template = templateTypeMap[type as TaskTemplateType](inputs, {
    task,
    customSubmit,
    setContinueButtonDisabled,
  });

  const defaultTemplate = (
    <div className={css(styles.defaultTemplate)}>
      <div>
        {showBackButton && (
          <span className={css(styles.backButtonWrapper)} data-dd-privacy="allow">
            <BackButton goBack={backToUserSelect} />
          </span>
        )}
        {!hideMessage && <TaskMessages messages={messages} />}
        {generalError && (
          <InputErrorWrapper id={'general-error'}>
            <Error error={generalError} />
          </InputErrorWrapper>
        )}
        {template}
        {alertInfo && (
          <AlertBox type={alertInfo.type} icon={alertInfo.icon} hideBottomBorder>
            {alertInfo.display}
          </AlertBox>
        )}
        {contextualHelps && <ContextualHelpList contextualHelps={contextualHelps} />}
      </div>
      <div data-dd-privacy="allow">
        {!hideContinueButton && !showExitButton && (
          <div className={css(styles.ctaWrapper)}>
            <DefaultSubmitInput disabled={isButtonDisabled} option={ctaButtonStyle}>
              {isButtonDisabled ? <LoadingSpinner size="sm" color={colors.white} /> : submitText}
            </DefaultSubmitInput>
          </div>
        )}
        {showExitButton && (
          <div className={css(styles.ctaWrapper)}>
            <ActionButton
              data-dd-privacy="allow"
              color={colors.catalyst}
              customStyle={styles.button}
              onClick={() => window.close()}
            >
              Exit window
            </ActionButton>
          </div>
        )}
      </div>
    </div>
  );

  // This onSubmit needs to be re-created every time, otherwise we will be submitting the old task
  return <TaskForm onSubmit={handleSubmit(customSubmit)} formTemplate={defaultTemplate} />;
};

const mapStateToProps = (state: State) => {
  return {
    task: state.tasks.current,
    initialValues: state.tasks.current?.template?.inputValues,
    values: getFormValues(FORM_NAME)(state),
  };
};

function mapDispatchToProps(dispatch: Dispatch) {
  return bindActionCreators(
    {
      submitCurrentTask: submitCurrentTaskAction,
      backToUserSelect: backToUserSelectAction,
    },
    dispatch,
  );
}

export const ConnectedTaskForm = connect<
  ConnectedTaskForm.StateProps,
  ConnectedTaskForm.DispatchProps,
  ConnectedTaskForm.OwnProps,
  State
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm<{}, ConnectedTaskForm.StateProps & ConnectedTaskForm.OwnProps & ConnectedTaskForm.DispatchProps>({
    form: FORM_NAME,
    enableReinitialize: true,
    keepDirtyOnReinitialize: true,
    onSubmit: async (values: any, dispatch: any, { task }: any) => {
      try {
        const { task: nextTask } = await participantAPI.tasks.submitTask(task.id, values);
        submitCurrentTaskAction(nextTask)(dispatch);
        onboardingPerformanceTracker.completeStep(task.id, task.templateId, true);
      } catch (error: unknown) {
        onboardingPerformanceTracker.completeStep(task.id, task.templateId, false);
        throw error; // Re-throw to let the form handle the error
      }
    },
  })(UnconnectedTaskForm),
);
